import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';

interface UseInfiniteUserResearchOptions {
  searchQuery?: string;
  limit?: number;
  excludeProjectId?: string; // Exclude research already assigned to this project
}

interface ResearchData {
  data: {
    id: string;
    title: string;
    content: string;
    research_type: string;
    created_at: string;
    updated_at: string;
    user_id: string;
    original_prompt: string | null;
    research_parameters: any;
    sources: any;
    related_questions: any;
    // Project assignment info (via workbook_id)
    workbook_id?: string | null;
    project?: {
      id: string;
      title: string;
    } | null;
    isAssignedToCurrentProject?: boolean;
  }[];
  nextPage?: number;
  hasMore: boolean;
  totalCount?: number;
}

export interface UserResearchWithProject {
  id: string;
  title: string;
  content: string;
  research_type: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  original_prompt: string | null;
  research_parameters: any;
  sources: any;
  related_questions: any;
  // Project assignment info
  project: {
    id: string;
    title: string;
  } | null;
  isAssignedToCurrentProject: boolean;
}

/**
 * Hook for infinite scrolling user research with search and project exclusion
 * UPDATED: Uses direct workbook_id instead of junction table
 * Shows research that are NOT assigned to the current project
 */
export const useInfiniteUserResearch = (options: UseInfiniteUserResearchOptions = {}) => {
  const { user } = useUser();
  const { searchQuery = '', limit = 50, excludeProjectId } = options;

  return useInfiniteQuery<ResearchData, Error, { pages: ResearchData[]; pageParams: number[] }, (string | number)[], number>({
    queryKey: ['infinite-user-research', user?.id, searchQuery, limit, excludeProjectId],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<ResearchData> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const offset = pageParam * limit;

      // Build query - matching the pattern used in images and pages
      let query = supabase
        .from('workbook_research')
        .select(`
          id,
          title,
          content,
          research_type,
          created_at,
          updated_at,
          user_id,
          original_prompt,
          research_parameters,
          sources,
          related_questions,
          workbook_id
        `, { count: 'exact' })
        .eq('user_id', user.id)
        .is('deleted_at', null) // Only active research
        .order('updated_at', { ascending: false });

      // Add search filter if provided
      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%,original_prompt.ilike.%${searchQuery}%`);
      }

      // Exclude research from a specific project if specified
      // We want to show: private research (workbook_id = null) + research from other projects
      if (excludeProjectId) {
        query = query.or(`workbook_id.is.null,workbook_id.neq.${excludeProjectId}`);
      }

      // Add pagination
      query = query.range(offset, offset + limit - 1);

      const { data: allResearch, error, count } = await query;

      if (error) {
        console.error('Error fetching user research:', error);
        throw error;
      }

      if (!allResearch || allResearch.length === 0) {
        return {
          data: [],
          totalCount: 0,
          hasMore: false,
          nextPage: undefined
        };
      }

      // Get project info for research with workbook_id
      const workbookIds = [...new Set(allResearch
        .map(r => r.workbook_id)
        .filter(Boolean))] as string[];
      
      let projectInfo: Record<string, { id: string; title: string }> = {};

      if (workbookIds.length > 0) {
        try {
          // Get workbook titles
          const { data: workbookData } = await supabase
            .from('workbook')
            .select('id, title')
            .in('id', workbookIds);

          if (workbookData) {
            // Map workbook_id to workbook info
            workbookData.forEach(workbook => {
              projectInfo[workbook.id] = {
                id: workbook.id,
                title: workbook.title
              };
            });
          }
        } catch (assignmentError) {
          console.warn('Error fetching project assignments:', assignmentError);
          // Continue without project assignment info
        }
      }

      // Transform the data
      const transformedData: UserResearchWithProject[] = allResearch.map(item => ({
        id: item.id,
        title: item.title,
        content: item.content,
        research_type: String(item.research_type), // Convert Json to string
        created_at: item.created_at,
        updated_at: item.updated_at,
        user_id: item.user_id,
        original_prompt: item.original_prompt,
        research_parameters: item.research_parameters,
        sources: item.sources,
        related_questions: item.related_questions,
        project: item.workbook_id ? projectInfo[item.workbook_id] || null : null,
        isAssignedToCurrentProject: item.workbook_id === excludeProjectId
      }));

      const totalCount = count || 0;
      const hasMore = allResearch.length === limit;

      return {
        data: transformedData,
        totalCount,
        hasMore,
        nextPage: hasMore ? pageParam + 1 : undefined
      };
    },
    getNextPageParam: (lastPage: ResearchData) => lastPage.nextPage,
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
}; 